import { useUserMe } from 'shared/api/userMeQuery'
import React, { useEffect, useRef, useState } from 'react'
import * as ChannelService from '@channel.io/channel-web-sdk-loader'
import type { BootOption } from '@channel.io/channel-web-sdk-loader'
import { useMediaQuery, useTheme } from '@mui/material'
import { useLocation } from 'react-router'
import { useSidebarOpenValue } from 'shared/hooks/useSidebarOpen'
import { SIDEBAR_DESKTOP_MIN_WIDTH } from 'shared/ui/Sidebar/constants'
import { getTenantNameFromAddressbar } from 'shared/utils/url'
import { useChannelTalkHashQuery } from 'shared/api/channelTalkHashQuery'

interface Props {
  children: React.ReactNode
  pluginKey?: string
}

export function isChannelTalkHiddenPath (pathname: string): boolean {
  const normalizedPathname = pathname.replace(/\/+$/, '') || '/'
  return normalizedPathname === '/viewer' ||
    normalizedPathname.startsWith('/viewer/') ||
    /^\/camera\/[^/]+$/.test(normalizedPathname)
}

/** ChannelTalk を無効化しているテナント */
const CHANNEL_TALK_EXCLUDED_TENANTS = ['sooki']

/** 指定テナントで ChannelTalk が無効化されているか */
export function isChannelTalkExcludedTenant (tenantName: string | null | undefined): boolean {
  return tenantName != null && CHANNEL_TALK_EXCLUDED_TENANTS.includes(tenantName)
}

/**
 * ChannelTalk へ送る表示名を組み立てる。
 * firstName / lastName が空・未設定でも壊れた名前（余分な空白だけ等）を作らないよう、
 * 有効な値だけを連結し、有効な名前が無ければ空文字を返す。
 */
export function buildChannelTalkName (
  firstName: string | null | undefined,
  lastName: string | null | undefined
): string {
  return [firstName, lastName]
    .filter((part): part is string => part != null && part.trim() !== '')
    .map((part) => part.trim())
    .join(' ')
}

export const ChannelTalkProvider: React.FC<Props> = (props: Props) => {
  const { children, pluginKey } = props
  const user = useUserMe()
  const { pathname } = useLocation()
  const tenantName = typeof window !== 'undefined' ? getTenantNameFromAddressbar(window.location.href) : null
  const isExcludedTenant = isChannelTalkExcludedTenant(tenantName)
  const isEnabled = (pluginKey != null && pluginKey !== '') && user != null && !isExcludedTenant
  const { data } = useChannelTalkHashQuery(isEnabled)
  const isBootingRef = useRef(false)
  const [isBooted, setIsBooted] = useState(false)
  const shouldHideInViewer = isChannelTalkHiddenPath(pathname)

  useEffect(() => {
    if (!isEnabled) {
      isBootingRef.current = false
      setIsBooted(false)
      return
    }

    if (data?.hash == null || data.hash === '') {
      return
    }

    // 既にbootされている場合はスキップ
    if (isBooted || isBootingRef.current) {
      return
    }

    // boot処理を開始することをマーク（重複実行防止）
    isBootingRef.current = true

    // 姓名が空・半端なときは name を送らず、ChannelTalk 側の既存の名前を上書きしない。
    // 有効な姓名があるときだけ最新値へ同期する。
    const channelTalkName = buildChannelTalkName(user.firstName, user.lastName)
    const bootOption: BootOption = {
      pluginKey,
      memberId: user.id,
      memberHash: data.hash,
      hideChannelButtonOnBoot: shouldHideInViewer,
      profile: {
        email: user.email,
        ...(channelTalkName !== '' ? { name: channelTalkName } : {}),
        tenantId: user.tenants?.[0]?.id ?? '',
        tenantName: user.tenants?.[0]?.name ?? ''
      }
    }

    const initializeChannelTalk = () => {
      try {
        ChannelService.loadScript()

        // bootを実行
        ChannelService.boot(bootOption, (error) => {
          if (error != null) {
            console.error('[ChannelTalk] Boot failed', {
              error,
              pluginKey: bootOption.pluginKey,
              memberId: bootOption.memberId
            })
            isBootingRef.current = false
            setIsBooted(false)
            return
          }
          isBootingRef.current = false
          setIsBooted(true)
        })
      } catch (error) {
        console.error('ChannelTalk SDK initialization failed', error)
        isBootingRef.current = false
        setIsBooted(false)
      }
    }

    initializeChannelTalk()
  }, [isEnabled, isBooted, data?.hash, pluginKey, user, isExcludedTenant, shouldHideInViewer])

  // Tablet 以下でサイドバー展開中は FAB が画面右下でサイドバー/一覧最下行と
  // 被ってクリック阻害するため、一時的に隠す
  const sidebarOpen = useSidebarOpenValue()
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up(SIDEBAR_DESKTOP_MIN_WIDTH))
  useEffect(() => {
    if (!isBooted) return
    const shouldHide = shouldHideInViewer || (sidebarOpen && !isDesktop)
    const applyChannelButtonVisibility = (): void => {
      try {
        if (shouldHide) {
          ChannelService.hideChannelButton()
        } else {
          ChannelService.showChannelButton()
        }
      } catch {
        // SDK がまだロード中など、例外は無視
      }
    }

    const pendingTimeouts: number[] = []
    const reapplyViewerHiddenState = (): void => {
      applyChannelButtonVisibility()
      pendingTimeouts.push(window.setTimeout(applyChannelButtonVisibility, 100))
    }

    applyChannelButtonVisibility()

    if (!shouldHideInViewer || typeof window === 'undefined') return

    window.addEventListener('resize', reapplyViewerHiddenState)
    window.addEventListener('focus', reapplyViewerHiddenState)
    document.addEventListener('visibilitychange', reapplyViewerHiddenState)
    // ChannelTalk SDK がルーティングやフォーカス復帰を契機にボタンを再表示することがあるため、
    // viewer パス滞在中は定期的に非表示を強制する。
    const visibilityInterval = window.setInterval(applyChannelButtonVisibility, 1000)

    return () => {
      window.removeEventListener('resize', reapplyViewerHiddenState)
      window.removeEventListener('focus', reapplyViewerHiddenState)
      document.removeEventListener('visibilitychange', reapplyViewerHiddenState)
      window.clearInterval(visibilityInterval)
      pendingTimeouts.forEach((timeoutId) => { window.clearTimeout(timeoutId) })
    }
  }, [isBooted, shouldHideInViewer, sidebarOpen, isDesktop])

  return <>{children}</>
}
